import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import RootFractionInput, {
  parseRootFractionInput,
  formatLatexPreview,
} from "../../../../components/inputs/RootFractionInput";

// (OTWARTE) - usuwanie niewymiernosci z mianownika:
// a/(k*sqrt(p)) lub (a*sqrt(q))/(k*sqrt(p))

const SQUAREFREE = [2, 3, 5, 6, 7, 10, 11];

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function gcd(a, b) {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) {
    [x, y] = [y, x % y];
  }
  return x || 1;
}

function coerceToString(val) {
  if (val == null) return "";
  if (typeof val === "string") return val;
  if (typeof val === "number" || typeof val === "boolean") return String(val);
  if (typeof val === "object" && val.target && "value" in val.target) {
    return String(val.target.value ?? "");
  }
  if (typeof val === "object" && "raw" in val) return String(val.raw ?? "");
  if (typeof val === "object" && "latex" in val) return String(val.latex ?? "");
  if (typeof val === "object" && "text" in val) return String(val.text ?? "");
  if (typeof val === "object" && "expr" in val) return String(val.expr ?? "");
  if (typeof val === "object" && "value" in val && typeof val.value !== "object") {
    return String(val.value ?? "");
  }
  return "";
}

function coerceToParsed(val) {
  if (val == null) return null;
  if (typeof val === "number" && Number.isFinite(val)) return val;
  if (typeof val === "object" && "parsed" in val && Number.isFinite(val.parsed)) {
    return val.parsed;
  }
  return null;
}

function simplifyRadicand(n) {
  const val = Math.abs(Number(n) || 0);
  if (!val) return { outside: 0, inside: 1 };

  let outside = 1;
  let inside = val;
  for (let i = Math.floor(Math.sqrt(val)); i >= 2; i--) {
    const sq = i * i;
    if (inside % sq === 0) {
      outside *= i;
      inside /= sq;
      i = Math.floor(Math.sqrt(inside)) + 1;
    }
  }
  return { outside, inside };
}

function normalizeCanonical(str) {
  return String(str ?? "")
    .replace(/\u200b/g, "")
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, "")
    .replace(/\s+/g, "")
    .replace(/,/g, ".")
    .replace(/[−–]/g, "-")
    .replace(/\\\\sqrt/g, "√")
    .replace(/\\sqrt/g, "√")
    .replace(/sqrt/g, "√")
    .replace(/√\{([^}]+)\}/g, "√$1")
    .replace(/√\(([^)]+)\)/g, "√$1")
    .trim();
}

function evaluateExpression(expr) {
  const cleaned = normalizeCanonical(expr);
  if (!cleaned) return NaN;
  try {
    const parsed = cleaned
      .replace(/\\frac\{([^{}]+)\}\{([^{}]+)\}/g, "($1)/($2)")
      .replace(/(\d*)√(\d+(\.\d+)?)/g, (_, a, b) => `${a || "1"}*Math.sqrt(${b})`)
      .replace(/√(\d+(\.\d+)?)/g, (_, b) => `Math.sqrt(${b})`)
      .replace(/sqrt\(([^)]+)\)/g, "Math.sqrt($1)")
      .replace(/[^0-9+\-*/().Mathsqrt]/g, "");
    return Function(`return ${parsed}`)();
  } catch {
    return NaN;
  }
}

function toSignature(str) {
  return String(str ?? "")
    .replace(/\u200b/g, "")
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, "")
    .replace(/\s+/g, "")
    .replace(/,/g, ".")
    .replace(/[−–]/g, "-")
    .replace(/\\left|\\right/g, "")
    .replace(/\\cdot/g, "")
    .replace(/\\d?frac\{([^{}]+)\}\{([^{}]+)\}/g, "($1)/($2)")
    .replace(/\\sqrt\{([^{}]+)\}/g, "sqrt($1)")
    .replace(/√\{([^{}]+)\}/g, "sqrt($1)")
    .replace(/√\(([^()]+)\)/g, "sqrt($1)")
    .replace(/√([0-9]+)/g, "sqrt($1)")
    .replace(/sqrt\{([^{}]+)\}/g, "sqrt($1)")
    .replace(/[{}]/g, "")
    .replace(/\*/g, "")
    .replace(/\((sqrt\(\d+(\.\d+)?\)|\d+(\.\d+)?)\)/g, "$1")
    .toLowerCase();
}

function stripOuterParens(s) {
  let out = String(s ?? "");
  while (out.startsWith("(") && out.endsWith(")")) {
    let depth = 0;
    let ok = true;
    for (let i = 0; i < out.length; i += 1) {
      if (out[i] === "(") depth += 1;
      if (out[i] === ")") depth -= 1;
      if (depth === 0 && i < out.length - 1) {
        ok = false;
        break;
      }
    }
    if (!ok) break;
    out = out.slice(1, -1);
  }
  return out;
}

function parseRadicalFractionForm(raw) {
  const src = normalizeCanonical(raw);
  if (!src) return null;

  const parts = src.split("/");
  if (parts.length > 2) return null;

  const numRaw = stripOuterParens(parts[0]);
  const denRaw = stripOuterParens(parts.length === 2 ? parts[1] : "1");

  const den = Number(denRaw);
  if (!Number.isFinite(den) || den === 0) return null;

  const numAsNumber = Number(numRaw);
  if (Number.isFinite(numAsNumber)) {
    return { coeff: numAsNumber, inside: 1, den };
  }

  const m = numRaw.match(/^([+-]?(?:\d+(?:\.\d+)?|\.\d+)?)?√(\d+(?:\.\d+)?)$/);
  if (!m) return null;

  const coeffRaw = m[1];
  let coeff = 1;
  if (coeffRaw === "-") coeff = -1;
  else if (coeffRaw && coeffRaw !== "+") {
    coeff = Number(coeffRaw);
    if (!Number.isFinite(coeff)) return null;
  }
  const inside = Number(m[2]);
  if (!Number.isFinite(inside)) return null;

  return { coeff, inside, den };
}

function buildExpectedSimpleForm(data) {
  const { rootNumerator, a, q, k, p } = data || {};
  if (!k || !p || !a) return null;

  const den = k * p;
  const rad = rootNumerator ? q * p : p;
  const { outside, inside } = simplifyRadicand(rad);

  let coeff = a * outside;
  let den2 = den;
  const g = gcd(coeff, den2);
  coeff /= g;
  den2 /= g;

  return { coeff, inside, den: den2 };
}

function sameSimpleForm(a, b) {
  if (!a || !b) return false;
  const eps = 1e-12;
  return (
    Math.abs(a.coeff - b.coeff) < eps &&
    Math.abs(a.inside - b.inside) < eps &&
    Math.abs(a.den - b.den) < eps
  );
}

function expectedNumericFromData(data) {
  const { rootNumerator, a, q, k, p } = data || {};
  if (!k || !p || !a) return NaN;
  if (!rootNumerator) return a / (k * Math.sqrt(p));
  if (!q) return NaN;
  return (a * Math.sqrt(q)) / (k * Math.sqrt(p));
}

function formatRationalizedLatex({ rootNumerator, a, q, k, p }) {
  const den = k * p;
  const rad = rootNumerator ? q * p : p;

  const { outside, inside } = simplifyRadicand(rad);
  let coeff = a * outside;
  let den2 = den;

  const g = gcd(coeff, den2);
  coeff /= g;
  den2 /= g;

  if (inside === 1) {
    if (den2 === 1) return `${coeff}`;
    return `\\frac{${coeff}}{${den2}}`;
  }

  const numLatex = coeff === 1 ? `\\sqrt{${inside}}` : `${coeff}\\sqrt{${inside}}`;
  if (den2 === 1) return numLatex;
  return `\\frac{${numLatex}}{${den2}}`;
}

export function generateTask() {
  const p = pick(SQUAREFREE);
  // Pozwalamy na mianownik typu sqrt(p) oraz k*sqrt(p),
  // ale częściej losujemy przypadki z k > 1 (np. 2sqrt(2), 3sqrt(3), 4sqrt(5)).
  const k = pick([1, 2, 2, 3, 3, 4]);

  // Sporadycznie pierwiastek w liczniku.
  const rootNumerator = Math.random() < 0.25;

  // Zeby wyrazenie startowe bylo od razu nieskracalne:
  // unikamy przypadkow typu 9 / (3√6), gdzie da sie skrocic przez 3.
  const numeratorPool = [2, 3, 4, 5, 6, 7, 8, 9];
  const coprimePool = numeratorPool.filter((n) => gcd(n, k) === 1);
  const a = pick(coprimePool.length ? coprimePool : numeratorPool);
  const q = rootNumerator ? pick(SQUAREFREE.filter((x) => x !== p)) : null;

  const numeratorLatex = rootNumerator ? `${a}\\sqrt{${q}}` : `${a}`;
  const denominatorLatex = k === 1 ? `\\sqrt{${p}}` : `${k}\\sqrt{${p}}`;

  const exprLatex = `\\frac{${numeratorLatex}}{${denominatorLatex}}`;
  const correctLatex = formatRationalizedLatex({ rootNumerator, a, q, k, p });

  return {
    type: "RationalizeSimpleDenominatorOpenTask",
    data: { rootNumerator, a, q, k, p, exprLatex, correctLatex },
    tags: ["dzialania", "pierwiastki", "usuniecie-niewymiernosci"],
  };
}

export function renderTask({ data }) {
  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>Usun niewymiernosc z mianownika:</p>
      <div style={{ marginTop: -6 }}>
        <BlockMath math={data.exprLatex} />
      </div>
      <p style={{ opacity: 0.85, marginTop: -6 }}>Zapisz wynik w najprostszej postaci.</p>
    </div>
  );
}

export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <RootFractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label=""
      previewPrefix="Podgląd:"
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  const raw = coerceToString(userAnswer);
  const parsedDirect = coerceToParsed(userAnswer);

  const expected = expectedNumericFromData(data);
  const parsedRoot = parseRootFractionInput(raw);
  const given = Number.isFinite(parsedDirect)
    ? parsedDirect
    : Number.isFinite(parsedRoot)
    ? parsedRoot
    : evaluateExpression(raw);

  if (!Number.isFinite(expected) || !Number.isFinite(given)) return false;
  if (Math.abs(expected - given) >= 0.01) return false;

  const expectedForm = buildExpectedSimpleForm(data);
  const givenForm = parseRadicalFractionForm(raw);
  if (sameSimpleForm(givenForm, expectedForm)) return true;

  const expectedSig = toSignature(data.correctLatex);
  const givenLatex = formatLatexPreview(raw, "") || raw;
  const givenSig = toSignature(givenLatex);
  if (expectedSig === givenSig) return true;

  return {
    correct: true,
    needsSimplification: true,
    messageType: "warning",
    message: `Wynik jest poprawny, ale mozna go uproscic. Najprostsza postac: $${data.correctLatex}$.`,
  };
}

export function renderAnswer({ data }) {
  return <InlineMath math={data.correctLatex} />;
}

export function renderUserAnswer(userAnswer) {
  const s = coerceToString(userAnswer);
  if (!s || s.trim() === "") return <i>(brak odpowiedzi)</i>;
  const latex = formatLatexPreview(s, "");
  return <InlineMath math={latex ? `\\displaystyle ${latex}` : normalizeCanonical(s)} />;
}

export function renderExplanation({ data }) {
  const { rootNumerator, a, q, k, p, exprLatex, correctLatex } = data;
  const denom = k === 1 ? `\\sqrt{${p}}` : `${k}\\sqrt{${p}}`;

  const multiplier = `\\frac{\\sqrt{${p}}}{\\sqrt{${p}}}`;
  const numBefore = rootNumerator ? `${a}\\sqrt{${q}}` : `${a}`;
  const numAfter = rootNumerator ? `${a}\\sqrt{${q * p}}` : `${a}\\sqrt{${p}}`;
  const denAfter = `${k * p}`;

  return (
    <div className="space-y-2">
      <p>
        Mnozymy licznik i mianownik przez <InlineMath math={`\\sqrt{${p}}`} />, aby usunac
        pierwiastek z mianownika.
      </p>
      <BlockMath math={`${exprLatex} \\cdot ${multiplier}`} />
      <BlockMath math={`= \\frac{${numBefore}\\cdot \\sqrt{${p}}}{${denom}\\cdot \\sqrt{${p}}}`} />
      <BlockMath math={`= \\frac{${numAfter}}{${denAfter}}`} />
      <p>Po uproszczeniu:</p>
      <BlockMath math={correctLatex} />
    </div>
  );
}
